import Button from "@dtci/spellbook/Button";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import moment from "moment";
import { usePPTActions } from "tap/store/ppt/usePPT";
import styles from "./pptDiscountClientPanel.module.scss";

/**
 * Master/detail "details" panel rendered when a Discount row is expanded.
 * One simple table — each row is a ClientDiscount; columns are the
 * cross-comparison attributes (client, financials, dates). Headers of
 * columns whose values DIVERGE across the children are highlighted so the
 * operator can spot the source of any rolled-up null at a glance. No
 * separate strip, no sub-grid — the grid IS the comparison.
 */

const formatDate = (v) => {
  if (v == null || v === "") return "";
  const m = moment(v);
  return m.isValid() ? m.format("MMM D, YYYY") : String(v);
};

const formatCurrency = (v, currency) => {
  if (v == null) return "—";
  return v.toLocaleString(undefined, {
    style: "currency",
    currency: currency || "USD",
  });
};

const formatPercent = (v) => {
  if (v == null) return "—";
  return v.toLocaleString(undefined, {
    style: "percent",
    maximumFractionDigits: 2,
  });
};

const SEVERITY_COLOR = {
  ERROR: "var(--sbt-error, #d94b4b)",
  WARNING: "var(--sbt-warning, #d9a648)",
  OK: "var(--sbt-success, #3a9b5b)",
};

const SeverityDot = ({ severity }) => (
  <span
    className={styles.severityDot}
    style={{
      backgroundColor: SEVERITY_COLOR[severity] || "var(--sbt-gray-40, #999)",
    }}
    title={severity || "Unknown"}
  />
);

/**
 * Column descriptors. `compareKey` is what we dedup across children to decide
 * whether the column varies. `numeric` flags right-aligned tabular columns.
 * `isComparison: false` (Status, Client) means this column never participates
 * in the highlight — it's expected to differ row-to-row.
 */
const COLUMNS = [
  {
    key: "severity",
    headerName: "",
    isComparison: false,
    width: 32,
    render: (c) => <SeverityDot severity={c.issueSeverity} />,
  },
  {
    key: "client",
    headerName: "Client",
    isComparison: false,
    render: (c) => c.client || c.clientLegalName || "—",
  },
  {
    key: "rateCard",
    headerName: "Rate Card",
    isComparison: true,
    compareKey: (c) => c.rateCardLabel ?? null,
    render: (c) => c.rateCardLabel || "—",
  },
  {
    key: "currency",
    headerName: "Currency",
    isComparison: true,
    compareKey: (c) => c.currency ?? null,
    render: (c) => c.currency || "—",
  },
  {
    key: "cost",
    headerName: "Cost",
    isComparison: true,
    numeric: true,
    compareKey: (c) => `${c.cost ?? ""}|${c.currency ?? ""}`,
    render: (c) => formatCurrency(c.cost, c.currency),
  },
  {
    key: "retail",
    headerName: "Retail",
    isComparison: true,
    numeric: true,
    compareKey: (c) => `${c.retail ?? ""}|${c.currency ?? ""}`,
    render: (c) => formatCurrency(c.retail, c.currency),
  },
  {
    key: "revShare",
    headerName: "Rev Share",
    isComparison: true,
    numeric: true,
    compareKey: (c) => c.revShare ?? null,
    render: (c) => formatPercent(c.revShare),
  },
  {
    key: "termStart",
    headerName: "Start Date",
    isComparison: true,
    compareKey: (c) => c.termStart ?? null,
    render: (c) => formatDate(c.termStart),
  },
  {
    key: "termEnd",
    headerName: "End Date",
    isComparison: true,
    compareKey: (c) => c.termEnd ?? null,
    render: (c) => formatDate(c.termEnd),
  },
];

export const PPTDiscountClientPanel = (params) => {
  // Grid context shows the explicit open-details icon; embedded in the
  // discount slide-out (showOpenAction=false) row click alone suffices.
  const showOpenAction = params.showOpenAction !== false;
  const children = Array.isArray(params?.data?.children)
    ? params.data.children
    : [];
  const parentDiscount = params?.data;
  const { openClientDiscountDrawer } = usePPTActions();

  const handleRowClick = useCallback(
    (clientDiscountRow) => {
      if (!parentDiscount || !clientDiscountRow) return;

      // Enrich child data with parent discount context for the drawer
      openClientDiscountDrawer({
        ...clientDiscountRow,
        title: parentDiscount.title,
        studio: parentDiscount.studio,
        format: parentDiscount.format,
        territory: parentDiscount.territory,
        media: parentDiscount.media,
        contentType: parentDiscount.contentType,
        contentClassification: parentDiscount.contentClassification,
        isBundle: parentDiscount.isBundle,
        radarProductId: parentDiscount.radarProductId,
        active: parentDiscount.active,
        parentDiscountId: parentDiscount.id,
      });
    },
    [openClientDiscountDrawer, parentDiscount],
  );

  // Which comparison columns vary across the children? Set of column keys.
  const varyingColumns = useMemo(() => {
    const result = new Set();
    if (children.length <= 1) return result;
    for (const col of COLUMNS) {
      if (!col.isComparison) continue;
      const values = new Set(children.map(col.compareKey));
      if (values.size > 1) result.add(col.key);
    }
    return result;
  }, [children]);

  // AG Grid's `detailRowAutoHeight: true` only works with the built-in detail
  // grid renderer; custom renderers (this one) have to push their measured
  // height back to AG Grid manually. Observe our root and propagate.
  const containerRef = useRef(null);
  useEffect(() => {
    const el = containerRef.current;
    const node = params?.node;
    const api = params?.api;
    if (!el || !node || !api) return;

    const apply = () => {
      const measured = el.offsetHeight;
      if (measured > 0 && measured !== node.rowHeight) {
        node.setRowHeight(measured);
        if (typeof api.onRowHeightChanged === "function") {
          api.onRowHeightChanged();
        }
      }
    };

    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(el);
    return () => observer.disconnect();
  }, [params?.node, params?.api, children.length]);

  if (children.length === 0) {
    return (
      <div ref={containerRef} className={styles.panel}>
        <div className={styles.empty}>No client discounts.</div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className={styles.panel}>
      <table className={styles.detailTable}>
        <thead>
          <tr>
            {COLUMNS.map((col) => {
              const varies = varyingColumns.has(col.key);
              const cls = [
                col.numeric ? styles.numCol : null,
                varies ? styles.varyingHeader : null,
              ]
                .filter(Boolean)
                .join(" ");
              return (
                <th
                  key={col.key}
                  className={cls}
                  style={col.width ? { width: col.width } : undefined}
                  title={varies ? "Values vary across clients" : undefined}
                >
                  {col.headerName}
                </th>
              );
            })}
            {showOpenAction ? (
              <th aria-label="Actions" style={{ width: 40 }} />
            ) : null}
          </tr>
        </thead>
        <tbody>
          {children.map((c) => {
            return (
              <tr
                key={c.id}
                onClick={(e) => handleRowClick(c)}
                className={styles.clickable}
              >
                {COLUMNS.map((col) => (
                  <td
                    key={col.key}
                    className={col.numeric ? styles.numCol : undefined}
                  >
                    {col.render(c)}
                  </td>
                ))}
                {showOpenAction ? (
                  <td>
                    <Button
                      styleType="flat"
                      colorType="secondary"
                      iconPlacement="before"
                      iconType="fa-arrow-up-right-from-square"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRowClick(c);
                      }}
                      title="Open details"
                      aria-label="Open details"
                      style={{ width: 30, height: 30 }}
                    />
                  </td>
                ) : null}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
